import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface TransferExtraItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  icon?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const useTransferExtraItems = () => {
  const queryClient = useQueryClient();

  // Fetch all extra items (for admin)
  const { data: allExtraItems, isLoading, error } = useQuery({
    queryKey: ['transfer-extra-items-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transfer_extra_items')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as TransferExtraItem[];
    },
  });

  // Fetch active extra items (for booking form)
  const { data: activeExtraItems } = useQuery({
    queryKey: ['transfer-extra-items-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transfer_extra_items')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as TransferExtraItem[];
    },
  });

  // Create extra item
  const createExtraItem = useMutation({
    mutationFn: async (item: Omit<TransferExtraItem, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('transfer_extra_items')
        .insert(item)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfer-extra-items-all'] });
      queryClient.invalidateQueries({ queryKey: ['transfer-extra-items-active'] });
      toast({ title: 'Success', description: 'Extra item created successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create extra item', variant: 'destructive' });
    },
  });

  // Update extra item
  const updateExtraItem = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<TransferExtraItem> & { id: string }) => {
      const { data, error } = await supabase
        .from('transfer_extra_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfer-extra-items-all'] });
      queryClient.invalidateQueries({ queryKey: ['transfer-extra-items-active'] });
      toast({ title: 'Success', description: 'Extra item updated successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update extra item', variant: 'destructive' });
    },
  });

  // Delete extra item
  const deleteExtraItem = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('transfer_extra_items')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfer-extra-items-all'] });
      queryClient.invalidateQueries({ queryKey: ['transfer-extra-items-active'] });
      toast({ title: 'Success', description: 'Extra item deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete extra item', variant: 'destructive' });
    },
  });

  return {
    allExtraItems,
    activeExtraItems,
    isLoading,
    error,
    createExtraItem,
    updateExtraItem,
    deleteExtraItem,
  };
};